import { useTranslation } from 'react-i18next';
import { ROUTES_ID } from '../routes/constants';
import { navigate } from '../routes/router';
import {
  changeQuantity,
  removeProduct,
  swapProduct,
} from '../sagas/sagaActions';
import { billingCadence } from '../shared/catalogLabels';
import { ErrorPage } from '../shared/ErrorPage';
import { Header } from '../shared/Header';
import { ArrowLeftIcon, CartIcon } from '../shared/icons';
import { LoadingOverlay } from '../shared/LoadingOverlay';
import {
  BenefitItem,
  NoticeList,
  OrderLineItems,
  OrderTotals,
  PriceTag,
  PromoCodeField,
  QuantityField,
  RemoveButton,
} from '../shared/primitives';
import { productIcon, productIconColor } from '../shared/productIcon';
import { selectHasError, selectReadOnly } from '../store/appSelectors';
import {
  type CartProductView,
  selectCartInitialized,
  selectCartLoading,
  selectCartProducts,
} from '../store/cartSelectors';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { selectCheckoutBlocked } from '../store/noticeSelectors';
import styles from './Cart.module.scss';
import { EmptyCart } from './EmptyCart';

function UpgradeBanner({
  upgrade,
  onUpgrade,
}: {
  upgrade: NonNullable<CartProductView['upgrade']>;
  onUpgrade: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className={styles.upgrade}>
      <div className={styles.upgradeGrid}>
        <div>
          <div className={styles.upgradeEyebrow}>{upgrade.eyebrow}</div>
          <div className={styles.upgradeTitle}>{upgrade.name}</div>
          <div className={styles.upgradeLogos}>
            {upgrade.logos.map((logo) => (
              <img
                key={logo.label}
                className={styles.upgradeLogo}
                src={productIcon(logo.icon)}
                alt={logo.label}
                title={logo.label}
              />
            ))}
          </div>
        </div>
        <div>
          <div className={styles.upgradeIncludesTitle}>{t('Includes')}</div>
          <ul className={styles.upgradeList}>
            {upgrade.includes.map((b) => (
              <BenefitItem key={b} label={b} />
            ))}
          </ul>
          <button
            type="button"
            className={styles.upgradeBtn}
            onClick={onUpgrade}
          >
            {t('Upgrade now')}
          </button>
        </div>
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className={`${styles.card} ${styles.skeletonCard}`} aria-hidden="true">
      <div className={styles.cardHead}>
        <div className={`${styles.skeleton} ${styles.skeletonLogo}`} />
        <div className={styles.intro}>
          <div className={`${styles.skeleton} ${styles.skeletonEyebrow}`} />
          <div className={`${styles.skeleton} ${styles.skeletonTitle}`} />
        </div>
      </div>

      <div className={`${styles.skeleton} ${styles.skeletonLabel}`} />
      <div className={styles.benefits}>
        <div className={styles.benefitCol}>
          <div className={`${styles.skeleton} ${styles.skeletonLine}`} />
          <div className={`${styles.skeleton} ${styles.skeletonLine}`} />
          <div className={`${styles.skeleton} ${styles.skeletonLine}`} />
        </div>
        <div className={styles.benefitCol}>
          <div className={`${styles.skeleton} ${styles.skeletonLine}`} />
          <div className={`${styles.skeleton} ${styles.skeletonLine}`} />
          <div className={`${styles.skeleton} ${styles.skeletonLine}`} />
        </div>
      </div>

      <div className={styles.divider} />

      <div className={`${styles.skeleton} ${styles.skeletonLabel}`} />
      <div className={styles.selectionRow}>
        <div className={`${styles.skeleton} ${styles.skeletonField}`} />
        <div className={styles.selectionPrice}>
          <div className={`${styles.skeleton} ${styles.skeletonPrice}`} />
        </div>
      </div>
    </div>
  );
}

function ProductCard({ view }: { view: CartProductView }) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const readOnly = useAppSelector(selectReadOnly);
  // Upgrading swaps products, which a read-only cart refuses.
  const upgrade = readOnly ? undefined : view.upgrade;

  const logoSrc = productIcon(view.logo);
  const half = Math.ceil(view.benefits.length / 2);
  const col1 = view.benefits.slice(0, half);
  const col2 = view.benefits.slice(half);
  const note = t('{{count}} license - billed {{cadence}}', {
    count: view.quantity,
    cadence: t(billingCadence(view.billingPeriod)),
  });
  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        {logoSrc && (
          <img className={styles.logo} src={logoSrc} alt={view.name} />
        )}
        <div className={styles.intro}>
          <div className={styles.eyebrow}>{view.eyebrow}</div>
          <div
            className={styles.title}
            style={{ color: productIconColor(view.logo) }}
          >
            {view.name}
          </div>
        </div>
      </div>

      <div className={styles.includesTitle}>{t('Includes')}</div>
      <div className={styles.benefits}>
        <ul className={styles.benefitCol}>
          {col1.map((b) => (
            <BenefitItem key={b} label={b} />
          ))}
        </ul>
        <ul className={styles.benefitCol}>
          {col2.map((b) => (
            <BenefitItem key={b} label={b} />
          ))}
        </ul>
      </div>

      <div className={styles.divider} />

      <div className={styles.selectionTitle}>{t('Your selection')}</div>
      <div className={styles.selectionRow}>
        <QuantityField
          value={view.quantity}
          minAmt={view.minAmt}
          maxAmt={view.maxAmt}
          onChange={(next) => dispatch(changeQuantity(view.ratePlan, next))}
        />
        <RemoveButton onClick={() => dispatch(removeProduct(view.ratePlan))} />
        <div className={styles.selectionPrice}>
          <PriceTag price={view.price} note={note} />
        </div>
      </div>

      {upgrade && (
        <UpgradeBanner
          upgrade={upgrade}
          onUpgrade={() =>
            dispatch(
              swapProduct(view.ratePlan, view.quantity, upgrade.ratePlan),
            )
          }
        />
      )}
    </div>
  );
}

function OrderSummary() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const blocked = useAppSelector(selectCheckoutBlocked);
  return (
    <div className={styles.summaryCard}>
      <OrderLineItems />
      <div className={styles.summaryPromo}>
        <PromoCodeField />
      </div>
      <div className={styles.summaryTotals}>
        <OrderTotals />
      </div>
      <button
        type="button"
        disabled={blocked}
        onClick={() => dispatch(navigate(ROUTES_ID.CHECKOUT))}
        className={styles.checkoutBtn}
      >
        <CartIcon className={styles.checkoutIcon} />
        {t('Continue with checkout')}
      </button>
    </div>
  );
}

export default function Cart() {
  const products = useAppSelector(selectCartProducts);
  const loading = useAppSelector(selectCartLoading);
  const initialized = useAppSelector(selectCartInitialized);
  const hasError = useAppSelector(selectHasError);
  const { t } = useTranslation();

  if (hasError) {
    return <ErrorPage />;
  }

  if (!initialized) {
    return (
      <div className={styles.page}>
        <Header />
        <div className={styles.fullPageLoading}>
          <LoadingOverlay />
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return <EmptyCart />;
  }

  return (
    <div className={styles.page}>
      <Header />
      <main className={styles.main}>
        {loading && <LoadingOverlay />}
        <a href={window.PRICING_PAGE} className={styles.backLink}>
          <ArrowLeftIcon className={styles.backIcon} />
          {t('Continue shopping')}
        </a>
        <div className={styles.layout}>
          <div className={styles.cards}>
            {products.map((view) =>
              loading && !view.hasData ? (
                <SkeletonCard key={view.ratePlan} />
              ) : (
                <ProductCard key={view.ratePlan} view={view} />
              ),
            )}
          </div>
          <aside className={styles.sidebar}>
            <NoticeList />
            <OrderSummary />
          </aside>
        </div>
      </main>
    </div>
  );
}
